// Latency-tab periods are independent of the shared AnalyticsPeriosOptions
// (which the other tabs use) so we can cap the widest window at 6 months.
export const LatencyPeriod = {
  TODAY: 'TODAY',
  WEEK: 'WEEK',
  MONTH: 'MONTH',
  HALF_YEAR: 'HALF_YEAR',
} as const;

export type LatencyPeriod = (typeof LatencyPeriod)[keyof typeof LatencyPeriod];

export const LatencyPeriodOptions = [
  { label: '6 months', value: LatencyPeriod.HALF_YEAR },
  { label: '30 days', value: LatencyPeriod.MONTH },
  { label: '7 days', value: LatencyPeriod.WEEK },
  { label: '24 hours', value: LatencyPeriod.TODAY },
];

/**
 * Metric presets pair a whitelisted ClickHouse column (`metric`) with the
 * `metric_type` that populates it, so the timeseries/percentiles queries stay
 * coherent (see the API §9 whitelist + the turn/component double-count note).
 */
export type LatencyMetricPreset = {
  value: string;
  label: string;
  metricType: string;
  metric: string;
  unit: string;
};

export const LatencyMetricPresets: LatencyMetricPreset[] = [
  {
    value: 'llm_ttft_ms',
    label: 'LLM TTFT',
    metricType: 'llm',
    metric: 'llm_ttft_ms',
    unit: 'ms',
  },
  {
    value: 'llm_duration_ms',
    label: 'LLM duration',
    metricType: 'llm',
    metric: 'llm_duration_ms',
    unit: 'ms',
  },
  {
    value: 'tts_ttfb_ms',
    label: 'TTS TTFB',
    metricType: 'tts',
    metric: 'tts_ttfb_ms',
    unit: 'ms',
  },
  {
    value: 'stt_duration_ms',
    label: 'STT duration',
    metricType: 'stt',
    metric: 'stt_duration_ms',
    unit: 'ms',
  },
  {
    value: 'latency_total_ms',
    label: 'Total latency (turn)',
    metricType: 'turn',
    metric: 'latency_total_ms',
    unit: 'ms',
  },

  // --- token counts ---
  {
    value: 'llm_prompt_tokens',
    label: 'Prompt tokens (LLM)',
    metricType: 'llm',
    metric: 'prompt_tokens',
    unit: 'tokens',
  },
  {
    value: 'llm_completion_tokens',
    label: 'Completion tokens (LLM)',
    metricType: 'llm',
    metric: 'completion_tokens',
    unit: 'tokens',
  },
  {
    value: 'llm_tokens_per_second',
    label: 'Tokens/sec (LLM)',
    metricType: 'llm',
    metric: 'tokens_per_second',
    unit: 'tok/s',
  },
  {
    value: 'rt_input_tokens',
    label: 'Input tokens (realtime)',
    metricType: 'realtime_turn',
    metric: 'input_tokens',
    unit: 'tokens',
  },
  {
    value: 'rt_output_tokens',
    label: 'Output tokens (realtime)',
    metricType: 'realtime_turn',
    metric: 'output_tokens',
    unit: 'tokens',
  },
  {
    value: 'rt_input_audio_tokens',
    label: 'Input audio tokens (realtime)',
    metricType: 'realtime_turn',
    metric: 'input_audio_tokens',
    unit: 'tokens',
  },
  {
    value: 'rt_input_text_tokens',
    label: 'Input text tokens (realtime)',
    metricType: 'realtime_turn',
    metric: 'input_text_tokens',
    unit: 'tokens',
  },
  {
    value: 'rt_input_cached_tokens',
    label: 'Input cached tokens (realtime)',
    metricType: 'realtime_turn',
    metric: 'input_cached_tokens',
    unit: 'tokens',
  },
  {
    value: 'rt_output_audio_tokens',
    label: 'Output audio tokens (realtime)',
    metricType: 'realtime_turn',
    metric: 'output_audio_tokens',
    unit: 'tokens',
  },
  {
    value: 'rt_output_text_tokens',
    label: 'Output text tokens (realtime)',
    metricType: 'realtime_turn',
    metric: 'output_text_tokens',
    unit: 'tokens',
  },
];

export const metricOptions = LatencyMetricPresets.map(p => ({
  value: p.value,
  label: p.label,
}));

export const findPreset = (value?: string): LatencyMetricPreset =>
  LatencyMetricPresets.find(p => p.value === value) ?? LatencyMetricPresets[0];

/** Bucket size per selected period, from the API's allowed interval set. */
export const intervalForPeriod = (period?: LatencyPeriod): string => {
  switch (period) {
    case LatencyPeriod.TODAY:
      return '15m';
    case LatencyPeriod.WEEK:
      return '1h';
    case LatencyPeriod.MONTH:
    case LatencyPeriod.HALF_YEAR:
      return '1d';
    default:
      return '1h';
  }
};

/** X-axis tick format per period (intraday vs multi-day). */
export const timeAxisFormat = (period?: LatencyPeriod): string =>
  period === LatencyPeriod.TODAY ? 'HH:mm' : 'MM-dd';
